<script lang="ts" setup>
import { useI18n } from "vue-i18n"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { computed, nextTick, ref } from "vue"
import { useFetchWrapper } from "@core/composables"
import { useVModel } from "@vueuse/core"

const emConsoCompareStore = useEmConsoCompareStore()
//===============================================
//                    Lang
//===============================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Étiquettes",
            description:
                "Sélectionnez les étiquettes à attribuer au bâtiment {name}\n parmi celles de l’entreprise à laquelle il appartient, ci-dessous.",
            help: "Comprendre le fonctionnement des étiquettes",
            helpMessage:
                "En les supprimant, toutes les données associées seront perdues. Êtes-vous sûr(e) de vouloir continuer ?",
            confirmButton: "Oui, supprimer",
            cancelButton: "Annuler",
            helpHeader: "Supprimer ces étiquettes ?",
            popupPlaceholder: "Nouvelle étiquette",
            validateButton: "Valider la sélection",
            leaveButton: "Quitter",
            deleteButton: "Valider la suppression",
        },
    },
})

//================================================
//                  Props
//================================================
type Props = {
    buildingId: string
    visible?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    visible: false,
})

const emit = defineEmits(["update:visible"])
const visible = useVModel(props, "visible", emit)

//================================================
//                 Variables
//================================================
const newTag = ref<string>("")
const removable = ref<boolean>(false)

const confirmationVisibility = ref<boolean>(false)
const popup = ref<
    | (HTMLElement & {
          show: (event?: any) => void
          hide: () => void
      })
    | null
>(null)
//Tags selectionnés de base (copy les tags du bâtiment)
const buildingTags = ref<string[]>([])
//Tags selectionnés (ajoutés au bâtiment)
const addedBuildingTags = ref<string[]>([])
//Tags déselectionnés (supprimer du bâtiment)
const removedBuildingTags = ref<string[]>([])
//Tags ajoutés à la company
const newCompanyTags = ref<string[]>([])
//Tags retirés de l'entreprise
const removedCompanyTags = ref<string[]>([])

//================================================
//                  onEvents Selection
//================================================
/**
 * Ajouter un tag à la liste des tags sélectionnés
 * Si le tag est selectionné, le retirer des ajouts
 * et l'ajouter aux removed
 * Si non, vérifier si le tag n'est pas deja dans la
 * liste des bâtiments et l'ajouter à la liste des
 * tags ajoutés, l'enlever de removed
 * @param tag - Tag à ajouter ou supprimer
 */
function toggleSelection(tag: any) {
    if (removable.value) return
    if (getSelectedTags.value.some((t) => t === tag)) {
        addedBuildingTags.value = addedBuildingTags.value.filter(
            (t: any) => t !== tag
        )
        if (buildingTags.value.some((t) => t === tag)) {
            removedBuildingTags.value.push(tag)
        }
    } else {
        if (!buildingTags.value.some((t) => t === tag)) {
            addedBuildingTags.value.push(tag)
        }
        removedBuildingTags.value = removedBuildingTags.value.filter(
            (t: any) => t !== tag
        )
    }
}

//================================================
//              Computed
//================================================
/**
 * Liste de tous les tags disponibles (excluant ceux supprimés ou en attente de suppression)
 */
const getAllTags = computed(() => {
    return [...emConsoCompareStore.tags, ...newCompanyTags.value].filter(
        (tag) => !removedCompanyTags.value.includes(tag)
    )
})

/**
 * Liste des tags sélectionnés (excluant ceux supprimés)
 */
const getSelectedTags = computed(() => {
    return [
        ...buildingTags.value.filter(
            (tag) => !removedBuildingTags.value.some((t) => t === tag)
        ),
        ...addedBuildingTags.value,
    ]
})

//================================================
//                  onEvents
//================================================
/**
 * Quand on clique sur un sous-lien pour afficher une popup
 * @param event - Événement de clic du bouton
 */
function onClickPopup(event: Event) {
    if (!popup.value) return

    popup.value.hide()

    nextTick(() => {
        if (popup.value) {
            popup.value.show(event)
        }
    })
}

//==================================================================
//                         Tag List events
//==================================================================
/**
 * Ajoute un tag à la liste des tags de la company.
 */
async function onClickAddTag() {
    const tagToAdd = newTag.value.trim()

    // Vérifier que le tag n'est pas vide et qu'il n'existe pas déjà
    if (tagToAdd.length === 0 || getAllTags.value.includes(tagToAdd)) return

    // Supprimer toutes les occurrences du tag dans pendingRemoval
    removedCompanyTags.value = removedCompanyTags.value.filter(
        (tag) => tag !== tagToAdd
    )

    // Ajouter le tag uniquement s'il n'est pas déjà dans les listes
    if (!newCompanyTags.value.includes(tagToAdd)) {
        newCompanyTags.value.push(tagToAdd)
    }
    if (!addedBuildingTags.value.includes(tagToAdd)) {
        addedBuildingTags.value.push(tagToAdd)
    }

    // Réinitialiser la valeur de newTag après ajout
    newTag.value = ""
    if (!popup.value) return
    popup.value.hide()
}

/**
 * Gère la modification des tags
 */
async function onClickModify() {
    removable.value = !removable.value
}

/**
 * Ajoute un tag à la liste des suppressions en attente
 */
function onRemove(tag: string) {
    if (!removedCompanyTags.value.includes(tag)) {
        removedCompanyTags.value.push(tag)
    }
}

//==================================================================
//                            Edit mode
//==================================================================

/**
 * Valide la suppression définitive des tags
 */
async function onDelete() {
    try {
        removable.value = false
        confirmationVisibility.value = false
        if (removedCompanyTags.value.length === 0) return

        // supprime les tags des bâtiments et des nouveaux tags ajoutés
        newCompanyTags.value = removeTagsFromList(newCompanyTags.value)
        addedBuildingTags.value = removeTagsFromList(addedBuildingTags.value)

        // si le tag était dans les tags de base de l'entreprise, on le supprime
        const tagsSet = new Set(emConsoCompareStore.tags)

        removedCompanyTags.value = removedCompanyTags.value.filter((tag) =>
            tagsSet.has(tag)
        )

        // Appel API pour SUPPRIMER les tags de l’entreprise
        if (removedCompanyTags.value.length > 0) {
            await emConsoCompareStore.deleteCompanyTag().fetch({
                tags: removedCompanyTags.value,
            })
            emConsoCompareStore.tags = emConsoCompareStore.tags.filter(
                (tag) => !removedCompanyTags.value.includes(tag)
            )
        }

        // 🔹 Réinitialisation des tags en attente
        removedCompanyTags.value = []
    } catch (error) {
        console.error(
            "La suppression des tags de l'entreprise n'a pas fonctionné",
            error
        )
    }
}

/**
 * Annule la suppression et réinitialise les tags
 */
function onCancel() {
    removedCompanyTags.value = []
    removable.value = false
}

//===================================================================
//                              Tools
//===================================================================
/**
 * Fonction pour supprimer les tags des listes concernés
 * @param list
 */
const removeTagsFromList = (list: any[]) =>
    list.filter((tag) => !removedCompanyTags.value.includes(tag))

//===================================================================
//                   Confirmation modal & calls api
//===================================================================
/**
 * Confirme les modifications des tags du bâtiment et de l’entreprise
 */
async function onConfirm() {
    performTagsAction.fetch()
}

const performTagsAction = useFetchWrapper(async () => {
    try {
        await Promise.all([
            addedBuildingTags.value.length > 0 ? handleAddTags() : null,
            removedBuildingTags.value.length > 0 ? handleRemoveTags() : null,
            newCompanyTags.value.length > 0 ? handleAddCompanyTags() : null,
            removedCompanyTags.value.length > 0
                ? handleRemoveCompanyTags()
                : null,
        ])

        updateBuildingTags()
        updateCompanyTags()
        onReset()
    } catch (error) {
        console.error("Erreur lors de la mise à jour des tags :", error)
    }
})

function onReset() {
    confirmationVisibility.value = false
    visible.value = false
    removable.value = false
    newCompanyTags.value = []
    addedBuildingTags.value = []
    removedBuildingTags.value = []
    removedCompanyTags.value = []
}

//=============================================================
//                     Handlers de call API
//=============================================================
/**
 * Gère l'ajout des tags au bâtiment
 */
async function handleAddTags() {
    return emConsoCompareStore.createBuildingTag().fetch({
        buildingId: props.buildingId,
        tags: addedBuildingTags.value,
    })
}

/**
 * Gère la suppression des tags du bâtiment
 */
async function handleRemoveTags() {
    return emConsoCompareStore.deleteBuildingTag().fetch({
        buildingId: props.buildingId,
        tags: removedBuildingTags.value,
    })
}

/**
 * Gère l'ajout des tags à l’entreprise
 */
async function handleAddCompanyTags() {
    if (!newCompanyTags.value.length) return

    return emConsoCompareStore.addCompanyTag().fetch({
        tagsToAdd: newCompanyTags.value,
    })
}

/**
 * Gère la suppression des tags de l’entreprise
 */
async function handleRemoveCompanyTags() {
    if (!removedCompanyTags.value.length) return

    return emConsoCompareStore.deleteCompanyTag().fetch({
        tagsToDelete: removedCompanyTags.value,
    })
}

//===================================================================
//                     Store update functions
//===================================================================
/**
 * Met à jour les tags du bâtiment dans le store
 */
function updateBuildingTags() {
    const newTagsSet = new Set([
        ...buildingTags.value,
        ...addedBuildingTags.value,
    ])

    removedBuildingTags.value.forEach((tag) => newTagsSet.delete(tag))

    const updatedTags = Array.from(newTagsSet).map((tag) => ({ name: tag }))

    // Mise à jour du store uniquement si les tags ont changé
    const buildingIndex = emConsoCompareStore.buildings.findIndex(
        (b) => b._id_building === props.buildingId
    )
    if (buildingIndex !== -1) {
        emConsoCompareStore.buildings.splice(buildingIndex, 1, {
            ...emConsoCompareStore.buildings[buildingIndex],
            tags: updatedTags,
        })
    }

    buildingTags.value = updatedTags.map((t) => t.name)
}

/**
 * Met à jour les tags de l’entreprise dans le store
 */
function updateCompanyTags() {
    const updatedCompanyTags = [
        ...emConsoCompareStore.tags.filter(
            (tag) => !removedCompanyTags.value.includes(tag)
        ),
        ...newCompanyTags.value,
    ]

    emConsoCompareStore.tags = [...new Set(updatedCompanyTags)]
}

//===================================================
//                    onMounted
//===================================================
function onShow() {
    const building = emConsoCompareStore.buildings.find(
        (b) => b._id_building === props.buildingId
    )

    if (building && Array.isArray(building.tags)) {
        buildingTags.value = building.tags.map((tag) => tag.name)
    } else {
        buildingTags.value = []
    }
}
</script>
<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        @hide="onReset"
        class="tag-modal"
        :pt="{
            header: 'tag-modal-header-global',
        }"
        postion="bottom"
        @show="onShow"
    >
        <template #header>
            <div class="tag-modal__header">
                <h2 class="text-xl font-bold">
                    {{ t("title") }}
                </h2>
                <p>{{ t("description") }}</p>
            </div>
        </template>
        <div class="tag-modal__content">
            <div class="tag-modal__content-list">
                <div class="tag-modal__content-list-tags">
                    <div
                        v-for="tag in getAllTags"
                        :key="tag"
                        class="tag-modal__content-list-chip"
                        :class="{
                            selected: getSelectedTags.includes(tag),
                        }"
                        @click="toggleSelection(tag)"
                    >
                        {{ tag }}
                        <i
                            v-if="
                                removable && !removedCompanyTags.includes(tag)
                            "
                            class="pi pi-times-circle"
                            @click="onRemove(tag)"
                        ></i>
                    </div>
                    <BaseButton
                        class="tag-modal__content-list-button-plus"
                        rounded
                        icon="pi pi-plus"
                        @click="onClickPopup"
                    >
                    </BaseButton>
                    <BaseButton
                        v-if="getAllTags.length > 0"
                        class="tag-modal__content-list-button-modify"
                        severity="secondary"
                        rounded
                        icon="pi pi-pencil"
                        @click="onClickModify"
                    ></BaseButton>
                </div>
                <BasePopover
                    ref="popup"
                    :pt="{
                        content: 'tag-modal__content-list-popover-content',
                    }"
                >
                    <div class="tag-modal__content-list-tags">
                        <BaseInputText
                            type="text"
                            v-model="newTag"
                            :placeholder="t('popupPlaceholder')"
                        />
                        <BaseButton
                            text
                            rounded
                            icon="pi pi-check"
                            @click="onClickAddTag"
                        ></BaseButton>
                    </div>
                </BasePopover>
                <div class="tag-modal__content-list-buttons" v-if="removable">
                    <BaseButton
                        text
                        :label="t('cancelButton')"
                        @click="onCancel"
                    ></BaseButton>
                    <BaseButton
                        severity="secondary"
                        :label="t('deleteButton')"
                        @click="
                            confirmationVisibility = !confirmationVisibility
                        "
                    >
                    </BaseButton>
                    <BaseDialog
                        v-model:visible="confirmationVisibility"
                        modal
                        :header="t('helpHeader')"
                        class="tag-modal__confirm-modal"
                    >
                        <div class="tag-modal__confirm-modal-content">
                            <i
                                class="pi pi-exclamation-triangle"
                                style="font-size: 2rem"
                            ></i>
                            {{ t("helpMessage") }}
                        </div>
                        <div class="tag-modal__confirm-modal-footer">
                            <BaseButton
                                icon="pi pi-trash"
                                :label="t('confirmButton')"
                                severity="secondary"
                                @click="onDelete"
                            ></BaseButton>
                            <BaseButton
                                :label="t('cancelButton')"
                                @click="
                                    confirmationVisibility =
                                        !confirmationVisibility
                                "
                            ></BaseButton>
                        </div>
                    </BaseDialog>
                </div>
            </div>
            <BaseButton
                class="tag-modal__help-button"
                icon="pi pi-question-circle"
                size="small"
                variant="link"
                :label="t('help')"
                severity="contrast"
            />
        </div>
        <template #footer>
            <div class="tag-modal__footer">
                <BaseButton
                    :label="t('leaveButton')"
                    text
                    @click="visible = !visible"
                ></BaseButton>
                <BaseButton
                    :label="t('validateButton')"
                    @click="onConfirm"
                    :loading="performTagsAction.isFetching.value"
                ></BaseButton>
            </div>
        </template>
    </BaseDialog>
</template>
<style scoped>
.tag-modal-add-button {
    width: 30px;
    height: 30px !important;
}
.tag-modal__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    & .tag-modal__help-button {
        --p-button-label-font-weight: 400;
        --p-button-link-color: var(--p-cyan-400);
        --p-button-link-hover-color: var(--p-cyan-500);
    }
}
.tag-modal__content-list {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    gap: 1rem;
    border-radius: 6px;
    border: 1px solid var(--surface-200, #eee);
    width: 100%;
    & .tag-modal__content-list-buttons {
        display: flex;
        justify-content: center;
        gap: 1rem;
        --p-button-text-primary-color: var(--p-cyan-400);
    }
}
.tag-modal__footer {
    width: 100%;
    display: flex;
    justify-content: space-between;
}
.tag-modal__header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.tag-modal__content-list-tags {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    & .tag-modal__content-list-chip {
        display: flex;
        height: 30px;
        padding: 5.25px 10.5px;
        align-items: center;
        gap: 7px;
        border-radius: 16px;
        background: #eee;
        cursor: pointer;
        tag-modal__content-list-chip.hover {
            background: var(--surface-300, #e0e0e0);
        }
    }
    & .tag-modal__content-list-chip.selected {
        border: 1px solid var(--chip-color, #4b5563);
        background: var(--surface-300, #e0e0e0);
    }
    & .tag-modal__content-list-button-plus {
        height: 30px !important;
        width: 30px;
        background: var(--p-cyan-400);
        border-color: var(--p-cyan-400);
        &:hover {
            background: var(--p-cyan-600) !important;
            border-color: var(--p-cyan-600) !important;
        }
    }
    & .tag-modal__content-list-button-modify {
        height: 30px !important;
        width: 30px;
    }
}

.tag-modal__confirm-modal {
    width: 600px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    & .tag-modal__confirm-modal-content {
        display: flex;
        padding-bottom: 1.75rem;
        gap: 0.9rem;
    }
    & .tag-modal__confirm-modal-footer {
        display: flex;
        flex-direction: row;
        gap: 0.5rem;
        justify-content: flex-end;
    }
}
</style>
<style>
.tag-modal {
    width: 800px !important;
}
.tag-modal__content-list-popover-content {
    background-color: #f9fafb;
}
.tag-modal-header-global {
    .p-dialog-header-actions {
        justify-content: flex-end;
        height: 1px !important;
    }
    align-items: normal !important;
    justify-content: space-between;
    flex-direction: column-reverse;
    h2 {
        text-align: center;
    }
    p {
        white-space: pre;
        text-align: center;
    }
}
</style>
